import { useState, useCallback, useEffect } from 'react';
import { deviceRegisterService } from '../services/posApi';
import {
    saveProductsToIndexedDB,
    needsSync,
    setLastSyncTime,
    getLastSyncTime,
    getProductsCount,
    clearProducts,
    isIndexedDBAvailable
} from '../utils/productsDB';

export const useProductsSync = () => {
    const [isSyncing, setIsSyncing] = useState(false);
    const [syncError, setSyncError] = useState(null);
    const [lastSyncTime, setLastSyncTimeState] = useState(null);
    const [productsCount, setProductsCount] = useState(0);

    // Обновление информации о последней синхронизации
    const updateSyncInfo = useCallback(async () => {
        try {
            const lastSync = getLastSyncTime();
            setLastSyncTimeState(lastSync);

            const count = await getProductsCount();
            setProductsCount(count);
        } catch (error) {
            console.error('Ошибка обновления информации о синхронизации:', error);
        }
    }, []);

    // Функция синхронизации товаров
    const syncProducts = useCallback(async (forceSync = false) => {
        // Проверяем доступность IndexedDB
        if (!isIndexedDBAvailable()) {
            const error = 'IndexedDB недоступен в этом браузере';
            console.error(error);
            setSyncError(error);
            return false;
        }

        // Проверяем, нужна ли синхронизация
        if (!forceSync && !needsSync()) {
            console.log('Синхронизация не требуется (менее 1 часа с последней синхронизации)');
            await updateSyncInfo();
            return true;
        }

        setIsSyncing(true);
        setSyncError(null);

        try {
            console.log('Начинаем синхронизацию товаров...');

            // Получаем товары с сервера
            const response = await deviceRegisterService.getRegisterProducts();

            if (!response || response.status !== 'success') {
                throw new Error('Неуспешный ответ от сервера: ' + (response?.status || 'неизвестная ошибка'));
            }

            if (!response.data || !Array.isArray(response.data)) {
                throw new Error('Неверный формат данных от сервера');
            }

            console.log(`Получено ${response.data.length} товаров с сервера`);

            // Сохраняем товары в IndexedDB
            await saveProductsToIndexedDB(response.data);

            // Обновляем время последней синхронизации
            setLastSyncTime();

            console.log('Синхронизация товаров завершена успешно');

            // Обновляем информацию о синхронизации
            await updateSyncInfo();

            return true;

        } catch (error) {
            console.error('Ошибка синхронизации товаров:', error);
            setSyncError(error.message || 'Неизвестная ошибка синхронизации');
            return false;
        } finally {
            setIsSyncing(false);
        }
    }, [updateSyncInfo]);

    // Принудительная синхронизация
    const forceSyncProducts = useCallback(async () => {
        return await syncProducts(true);
    }, [syncProducts]);

    // Очистка товаров
    const clearProductsData = useCallback(async () => {
        try {
            setIsSyncing(true);
            setSyncError(null);

            await clearProducts();
            await updateSyncInfo();

            console.log('Данные товаров очищены');
            return true;
        } catch (error) {
            console.error('Ошибка очистки товаров:', error);
            setSyncError(error.message || 'Ошибка очистки данных');
            return false;
        } finally {
            setIsSyncing(false);
        }
    }, [updateSyncInfo]);

    // Автоматическая синхронизация при инициализации
    const initSync = useCallback(async () => {
        // Автоматическая синхронизация только если есть авторизация устройства
        try {
            // Импортируем deviceAuthService динамически, чтобы избежать циклических зависимостей
            const { deviceAuthService } = await import('../services/posApi');

            if (deviceAuthService && deviceAuthService.isAuthenticated()) {
                await syncProducts(false);
            }
        } catch (error) {
            console.error('Ошибка автоматической синхронизации:', error);
        }
    }, [syncProducts]);

    // Инициализация при первом рендере
    useEffect(() => {
        updateSyncInfo();
    }, [updateSyncInfo]);

    return {
        // Состояние
        isSyncing,
        syncError,
        lastSyncTime,
        productsCount,

        // Методы
        syncProducts,
        forceSyncProducts,
        clearProductsData,
        initSync,
        updateSyncInfo,

        // Утилиты
        needsSync: needsSync(),
        isIndexedDBAvailable: isIndexedDBAvailable()
    };
};